import React, { useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const emptyPayment = () => ({
  mode: 'none',            // 'none' | 'full' | 'monthly'
  total_ex_vat: '',
  vat_rate: '20',
  num_months: '12',
  months_paid: '0',
  deposit_ex_vat: '0',     // monthly only; installments cover total - deposit
});

// -> undefined when nothing should be sent; object for the API otherwise.
export const buildPaymentPayload = (p) => {
  if (!p || p.mode === 'none') return undefined;
  const payload = {
    mode: p.mode,
    total_ex_vat: parseFloat(p.total_ex_vat) || 0,
    vat_rate: parseFloat(p.vat_rate) || 0,
  };
  if (p.mode === 'monthly') {
    payload.num_months = parseInt(p.num_months, 10) || 0;
    payload.months_paid = parseInt(p.months_paid, 10) || 0;
    payload.deposit_ex_vat = parseFloat(p.deposit_ex_vat) || 0;
  }
  return payload;
};

// Pre-fills amounts from the selected course; admin can override every field.
export const EnrollmentPaymentFields = ({ value, onChange, course }) => {
  useEffect(() => {
    if (course && value.total_ex_vat === '') {
      onChange({
        ...value,
        total_ex_vat: String(course.price ?? ''),
        vat_rate: String(course.vat_rate ?? 20),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course?.course_id]);

  const set = (field) => (e) => onChange({ ...value, [field]: e.target.value });
  const months = parseInt(value.num_months, 10) || 0;
  const paid = parseInt(value.months_paid, 10) || 0;
  const total = parseFloat(value.total_ex_vat) || 0;
  const vat = parseFloat(value.vat_rate) || 0;
  const deposit = value.mode === 'monthly' ? parseFloat(value.deposit_ex_vat) || 0 : 0;
  const totalIncVat = total * (1 + vat / 100);
  const depositIncVat = deposit * (1 + vat / 100);
  // Monthly installments are calculated on the balance remaining after the deposit.
  const remainingExVat = Math.max(total - deposit, 0);
  const perMonth = months > 0 ? (remainingExVat * (1 + vat / 100)) / months : 0;
  const remaining = perMonth * Math.max(months - paid, 0);
  const depositTooBig = deposit > 0 && deposit >= total && total > 0;

  return (
    <div className="space-y-3 border-t pt-4" data-testid="enrollment-payment-fields">
      <Label className="font-medium">Payment</Label>
      <div className="flex gap-4">
        {[['none', 'No payment recorded'], ['full', 'Paid in full'], ['monthly', 'Monthly plan']].map(
          ([mode, label]) => (
            <label key={mode} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="payment-mode"
                checked={value.mode === mode}
                onChange={() => onChange({ ...value, mode })}
                data-testid={`payment-mode-${mode}`}
              />
              {label}
            </label>
          )
        )}
      </div>

      {value.mode !== 'none' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Total (ex VAT) £</Label>
            <Input type="number" min="0" step="0.01" value={value.total_ex_vat}
                   onChange={set('total_ex_vat')} data-testid="payment-total" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">VAT %</Label>
            <Input type="number" min="0" step="0.1" value={value.vat_rate}
                   onChange={set('vat_rate')} data-testid="payment-vat" />
          </div>
          {value.mode === 'monthly' && (
            <>
              <div>
                <Label className="text-xs text-slate-500">Deposit paid (ex VAT) £</Label>
                <Input type="number" min="0" step="0.01" value={value.deposit_ex_vat}
                       onChange={set('deposit_ex_vat')} data-testid="payment-deposit" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Number of months</Label>
                <Input type="number" min="1" value={value.num_months}
                       onChange={set('num_months')} data-testid="payment-months" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Installments already paid</Label>
                <Input type="number" min="0" max={months} value={value.months_paid}
                       onChange={set('months_paid')} data-testid="payment-months-paid" />
              </div>
            </>
          )}
        </div>
      )}

      {value.mode === 'full' && total > 0 && (
        <p className="text-sm text-slate-500">£{totalIncVat.toFixed(2)} inc VAT — recorded as paid.</p>
      )}
      {value.mode === 'monthly' && depositTooBig && (
        <p className="text-sm text-red-600">
          Deposit must be less than the total — use “Paid in full” if everything is paid.
        </p>
      )}
      {value.mode === 'monthly' && months > 0 && total > 0 && !depositTooBig && (
        <p className="text-sm text-slate-500">
          {deposit > 0 && <>Deposit £{depositIncVat.toFixed(2)} inc VAT · </>}
          {paid} of {months} paid · £{perMonth.toFixed(2)}/month · £{remaining.toFixed(2)} remaining
        </p>
      )}
    </div>
  );
};

export default EnrollmentPaymentFields;
